import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Lock, UserCog, Trash2, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "./ConfirmProvider";

const input = "w-full px-4 py-3 rounded-xl border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand bg-white";

const PwField = ({ label, value, onChange, show, onToggle, testid }) => (
  <div>
    <label className="block text-sm font-medium text-stone-sub mb-1">{label}</label>
    <div className="relative">
      <input data-testid={testid} type={show ? "text" : "password"} value={value} onChange={onChange} className={input + " pr-11"} />
      <button type="button" data-testid={`${testid}-toggle`} onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-muted hover:text-brand" tabIndex={-1}>
        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  </div>
);

// Account control for ALL roles: edit name/phone + change password.
export const AccountSettings = () => {
  const { t } = useTranslation();
  const { user, refresh, logout } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [info, setInfo] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [savingInfo, setSavingInfo] = useState(false);
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const setI = (k) => (e) => setInfo((f) => ({ ...f, [k]: e.target.value }));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => () => setShow((s) => ({ ...s, [k]: !s[k] }));

  const handleErr = (err, notDeployedKey) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 404 || status === 405) toast.error(t(notDeployedKey));
    else if (typeof detail === "string") toast.error(detail);
    else if (!err?.response) toast.error(t("auth.networkError"));
    else toast.error(t("common.error"));
  };

  const saveInfo = async (e) => {
    e.preventDefault();
    setSavingInfo(true);
    try {
      await api.put("/auth/me", { name: info.name.trim(), phone: info.phone.trim() });
      await refresh();
      toast.success(t("account.infoSaved"));
    } catch (err) { handleErr(err, "account.infoNotDeployed"); }
    finally { setSavingInfo(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (form.next.length < 6) { toast.error(t("account.passwordTooShort")); return; }
    if (form.next !== form.confirm) { toast.error(t("account.passwordMismatch")); return; }
    setSaving(true);
    try {
      await api.put("/auth/password", { current_password: form.current, new_password: form.next });
      toast.success(t("account.passwordChanged"));
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) { handleErr(err, "account.notDeployed"); }
    finally { setSaving(false); }
  };

  const deleteAccount = async () => {
    if (!(await confirm({
      title: t("account.deleteTitle"),
      description: t("account.deleteConfirm"),
      confirmText: t("account.deleteAccount"),
    }))) return;
    try {
      await api.delete("/auth/me");
      toast.success(t("account.deleted"));
      logout();
      navigate("/");
    } catch (err) { handleErr(err, "account.deleteNotDeployed"); }
  };

  return (
    <div className="space-y-6" data-testid="account-settings">
      <div className="bg-white rounded-2xl border border-stone-border shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-brand-soft flex items-center justify-center"><UserCog className="w-5 h-5 text-brand" /></div>
          <h2 className="font-heading text-xl font-bold text-stone-text">{t("account.info")}</h2>
        </div>
        <p className="text-sm text-stone-muted mb-4">{t("account.infoSubtitle")}</p>
        <form onSubmit={saveInfo} className="space-y-4" data-testid="account-info-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-sub mb-1">{t("account.name")}</label>
              <input data-testid="account-name" required className={input} value={info.name} onChange={setI("name")} />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-sub mb-1">{t("account.phone")}</label>
              <input data-testid="account-phone" className={input} value={info.phone} onChange={setI("phone")} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-sub mb-1">{t("account.email")}</label>
            <input data-testid="account-email" disabled value={user?.email || ""} className={input + " bg-stone-bg text-stone-muted cursor-not-allowed"} />
            {user?.email_verified === true && (
              <span className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full bg-housing/15 text-housing text-xs font-semibold" data-testid="verified-badge">
                <BadgeCheck className="w-4 h-4" /> {t("auth.verify.verified")}
              </span>
            )}
          </div>
          <button data-testid="account-save-info" type="submit" disabled={savingInfo} className="px-6 py-3 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors disabled:opacity-60">
            {savingInfo ? "..." : t("account.saveInfo")}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-2xl border border-stone-border shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-brand-soft flex items-center justify-center"><Lock className="w-5 h-5 text-brand" /></div>
          <h2 className="font-heading text-xl font-bold text-stone-text">{t("account.title")}</h2>
        </div>
        <p className="text-sm text-stone-muted mb-4">{t("account.subtitle")}</p>
        <form onSubmit={savePassword} className="space-y-4" data-testid="account-password-form">
          <PwField label={t("account.currentPassword")} value={form.current} onChange={set("current")} show={show.current} onToggle={toggle("current")} testid="account-current-password" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PwField label={t("account.newPassword")} value={form.next} onChange={set("next")} show={show.next} onToggle={toggle("next")} testid="account-new-password" />
            <PwField label={t("account.confirmPassword")} value={form.confirm} onChange={set("confirm")} show={show.confirm} onToggle={toggle("confirm")} testid="account-confirm-password" />
          </div>
          <button data-testid="account-save-password" type="submit" disabled={saving} className="px-6 py-3 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors disabled:opacity-60">
            {saving ? "..." : t("account.changePassword")}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-2xl border border-red-200 shadow-sm p-6 sm:p-8" data-testid="danger-zone">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center"><Trash2 className="w-5 h-5 text-red-600" /></div>
          <h2 className="font-heading text-xl font-bold text-red-700">{t("account.dangerTitle")}</h2>
        </div>
        <p className="text-sm text-stone-muted mb-4">{t("account.dangerDesc")}</p>
        <button data-testid="delete-account-btn" onClick={deleteAccount} className="px-6 py-3 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors">
          {t("account.deleteAccount")}
        </button>
      </div>
    </div>
  );
};
